import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { session } from '@/lib/api/session';
import { SESSION_EXPIRED_EVENT } from '@/lib/api/client';

/**
 * The web app's single realtime socket to the WebSocket server (apps/websocket).
 *
 * The server authenticates the handshake with the JWT access token and joins the
 * connection to its `user:<id>` / `tenant:<id>` rooms; the API publishes events
 * (e.g. `notification:new`) via Redis and the WS server fans them to those rooms.
 * This client is the missing consumer — before it, every realtime event was
 * published server-side and dropped on the floor, leaving the bell poll-only.
 *
 * Design notes:
 *  • ONE shared socket for the whole app (a singleton), not one per component.
 *  • `auth` is a callback so the CURRENT access token is read on every (re)connect
 *    — after a token refresh rotates it, the next reconnect uses the fresh one.
 *  • Best-effort: if the WS server / Redis is down the socket simply keeps
 *    retrying in the background; feature code keeps its polling fallback.
 */

let socket: Socket | null = null;

function getSocket(): Socket {
  if (socket) return socket;
  // No URL → same origin ('/socket.io'), proxied to :4001 in dev, served behind
  // the same host in prod — mirrors how '/api' stays relative.
  socket = io({
    path: '/socket.io',
    autoConnect: false,
    auth: (cb) => cb({ token: session.accessToken ?? '' }),
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
  });
  return socket;
}

/**
 * The shared socket, for consumers that need raw emit/on/off (e.g. FR-24's
 * `useWebRTCCall`, which juggles several ad-hoc signaling events at once —
 * more than the single-event `useRealtimeEvent` hook below is meant for).
 * Every other consumer should prefer `useRealtimeEvent`/`useRoomMembership`.
 */
export function getSocketForCalls(): Socket {
  return getSocket();
}

/** Open the shared socket if we're signed in (no-op if already connecting/open). */
export function connectRealtime(): void {
  if (!session.isAuthenticated) return;
  const s = getSocket();
  if (!s.active) s.connect();
}

/** Close the shared socket (e.g. on sign-out). */
export function disconnectRealtime(): void {
  socket?.disconnect();
}

// A dead session must not keep a socket alive with a stale identity.
if (typeof window !== 'undefined') {
  window.addEventListener(SESSION_EXPIRED_EVENT, disconnectRealtime);
}

/**
 * Own the shared connection for as long as the authenticated shell is mounted.
 * Mounted once at the app frame — connects on entry, disconnects on sign-out.
 */
export function useRealtimeConnection(): void {
  useEffect(() => {
    connectRealtime();
    return () => disconnectRealtime();
  }, []);
}

/**
 * Subscribe a component to a realtime event for its lifetime. The latest
 * `handler` is always used without resubscribing on every render.
 */
export function useRealtimeEvent<T = unknown>(event: string, handler: (payload: T) => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const s = getSocket();
    const fn = (payload: T) => ref.current(payload);
    s.on(event, fn as (...args: unknown[]) => void);
    return () => {
      s.off(event, fn as (...args: unknown[]) => void);
    };
  }, [event]);
}

// ── FR-23: project-chat/video-call rooms ──
// Unlike tenant/user rooms (auto-joined server-side on connect), a
// conversation/call room requires an explicit client join — the server
// authorizes it per-join against real membership (see
// apps/websocket/src/rooms.js). This is the client's first EMIT (every
// prior use of this module was subscribe-only).

export type RoomKind = 'conversation' | 'call';
export interface RoomJoinAck {
  ok: boolean;
  error?: string;
}

function emitJoin(kind: RoomKind, id: string): Promise<RoomJoinAck> {
  return new Promise((resolve) => {
    getSocket().emit('room:join', { kind, id }, (ack: RoomJoinAck) => resolve(ack ?? { ok: false, error: 'no_ack' }));
  });
}

function emitLeave(kind: RoomKind, id: string): void {
  getSocket().emit('room:leave', { kind, id });
}

/**
 * Joins the given conversation/call room on mount (once the socket is
 * connected and `id` is set) and leaves it on unmount or when `id` changes.
 * `onJoined`/`onDenied` fire with the join result for callers that care
 * (e.g. showing "you can't join this call").
 */
export function useRoomMembership(
  kind: RoomKind,
  id: string | undefined,
  handlers?: { onJoined?: () => void; onDenied?: (error?: string) => void },
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const s = getSocket();

    const join = () =>
      emitJoin(kind, id).then((ack) => {
        if (cancelled) return;
        if (ack.ok) handlersRef.current?.onJoined?.();
        else handlersRef.current?.onDenied?.(ack.error);
      });

    if (s.connected) join();
    s.on('connect', join);

    return () => {
      cancelled = true;
      s.off('connect', join);
      emitLeave(kind, id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);
}
